import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import useBotStrategyStore from "../../stores/bot";
import usePairsStore from "../../stores/courses/pair";
import Dropdown from "../../components/commons/Dropdown";
import SuccessModal from "./SuccessModal";

const CreateBotModal = ({ show, onClose }) => {
  const {
    createBotProfile,
    getBotMasters,
    botMasters,
    isLoading,
    isSubmitting,
  } = useBotStrategyStore();

  const {
    getPairs,
    loadMorePairs,
    searchCoins,
    pairs: coins,
    isCoinLoading,
    isLoadingMore,
    pagination,
  } = usePairsStore();

  const [form, setForm] = useState({
    name: "",
    description: "",
    parameter_level: "low",
    bot_master_id: "",
    coin_id: "",
    parameters: {},
  });

  const [parameterFields, setParameterFields] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  // Store selected coin details separately
  const [selectedCoin, setSelectedCoin] = useState(null);

  const PARAMETER_MAP = {
    GRID: [
      "grid_width",
      "grid_width_exit",
      "buy_after_bounce",
      "sell_after_bounce",
      "number_of_grids",
      "amount",
      "trade_size",
    ],
    "XENO ARC": [
      "grid_width",
      "buy_flg_return",
      "grid_width_exit",
      "exit_flg_return",
      "capital",
      "number_of_grids",
      "amount",
    ],
    DCA: [
      "take_profit_percentage",
      "price_change_percentage",
      "trigger_price",
      "period",
      "max_order_count",
      "capital",
      "amount",
    ],
  };

  useEffect(() => {
    if (show) {
      getBotMasters();
      // Load initial coins with larger page size and clear any previous filters
      getPairs(
        { exchange_id: null, target_currency: "", base_currency: "" },
        { page: 1, page_size: 50 }
      );
    } else {
      // Reset selected coin when modal closes
      setSelectedCoin(null);
    }
  }, [show]);

  const handleSelectBotMaster = (opt) => {
    setForm((prev) => ({ ...prev, bot_master_id: opt.id }));
    const master = botMasters.find((b) => b.id === opt.id);
    if (master) {
      const key = Object.keys(PARAMETER_MAP).find((k) =>
        master.bot_name.toUpperCase().includes(k)
      );
      if (key) {
        const paramsObj = PARAMETER_MAP[key].reduce((acc, field) => {
          acc[field] = "";
          return acc;
        }, {});
        setParameterFields(PARAMETER_MAP[key]);
        setForm((prev) => ({ ...prev, parameters: paramsObj }));
      } else {
        setParameterFields([]);
        setForm((prev) => ({ ...prev, parameters: {} }));
      }
    }
  };

  const handleParameterChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [key]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    const formattedParameters = Object.entries(form.parameters).map(
      ([key, value]) => ({
        parameter_key: key,
        parameter_value: value === "" ? null : value,
      })
    );

    const payload = {
      name: form.name,
      description: form.description,
      parameter_level: form.parameter_level,
      bot_master_id: Number(form.bot_master_id),
      coin_id: Number(form.coin_id),
      parameters: formattedParameters,
    };

    const result = await createBotProfile(payload);
    if (result) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 1800);
    } else {
      alert("Failed to create bot.");
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed z-50 inset-0 bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded-lg w-[45rem] shadow-xl p-6 overflow-y-auto max-h-[90vh] relative">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Create New Bot
            </h2>
            <button onClick={onClose}>
              <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700" />
            </button>
          </div>

          <div className="space-y-4">
            <input
              type="text"
              placeholder="Bot Name"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <textarea
              placeholder="Description"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />

            <div className="grid grid-cols-2 gap-3">
              {/* Bot Master Dropdown - Client-side search (no API) */}
              <Dropdown
                label="Select Bot Master"
                placeholder="Choose bot type..."
                withSearch
                value={
                  form.bot_master_id
                    ? {
                        id: form.bot_master_id,
                        name: botMasters.find(
                          (b) => b.id === form.bot_master_id
                        )?.bot_name,
                      }
                    : null
                }
                onChange={handleSelectBotMaster}
                options={botMasters.map((b) => ({
                  id: b.id,
                  name: b.bot_name,
                }))}
                loading={isLoading}
                clearable
                searchPlaceholder="Filter bot types..."
              />

              {/* Coin Dropdown - API search with infinite scroll */}
              <Dropdown
                label="Select Coin"
                placeholder="Choose a coin..."
                withSearch
                value={selectedCoin}
                onChange={(opt) => {
                  if (opt) {
                    setForm({ ...form, coin_id: opt.id });
                    // Store the full coin details
                    setSelectedCoin(opt);
                  } else {
                    // Handle clear
                    setForm({ ...form, coin_id: "" });
                    setSelectedCoin(null);
                  }
                }}
                options={coins.map((c) => ({
                  id: c.id,
                  name: `${c.coin} / ${c.base_currency} (${c.exchange})`,
                }))}
                loading={isCoinLoading}
                loadingMore={isLoadingMore}
                hasMore={!!pagination.next}
                onLoadMore={loadMorePairs}
                onSearchChange={searchCoins}
                searchDebounceMs={500}
                searchPlaceholder="Search coins (e.g., BTC/USDT)..."
                clearable
              />
            </div>

            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={form.parameter_level}
              onChange={(e) =>
                setForm({ ...form, parameter_level: e.target.value })
              }
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>

            {parameterFields.length > 0 && (
              <>
                <p className="text-sm font-medium text-gray-700 text-left">
                  Parameters
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {parameterFields.map((field) => (
                    <div key={field}>
                      <label className="block text-xs text-gray-500 mb-2 capitalize text-left">
                        {field.replaceAll("_", " ")}
                      </label>
                      <input
                        type="number"
                        step="any"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={form.parameters[field] || ""}
                        onChange={(e) =>
                          handleParameterChange(field, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:bg-blue-400"
            >
              {isSubmitting ? "Creating..." : "Create Bot"}
            </button>
          </div>
        </div>
      </div>

      <SuccessModal
        show={showSuccess}
        message="Bot Created Successfully!"
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};

export default CreateBotModal;
